import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, NavLink } from 'react-router-dom';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

import AlertDashboard from './pages/AlertDashboard';
import AlertSettings  from './pages/AlertSettings';
import DeviceMappings from './pages/DeviceMappings';
import AlertHistory   from './pages/AlertHistory';
import DeliveryStatus from './pages/DeliveryStatus';
import Login from './pages/Login';
import { clearAuthToken, getAuthToken, getCurrentUser } from './api/alertApi';

const qc = new QueryClient({ defaultOptions: { queries: { refetchInterval: 30000, staleTime: 10000 } } });

const NAV_ITEMS = [
  { to: '/',          label: 'Dashboard' },
  { to: '/settings',  label: 'Settings'  },
  { to: '/devices',   label: 'Devices'   },
  { to: '/history',   label: 'Alert History' },
  { to: '/delivery',  label: 'Delivery Status' },
];

export default function App() {
  const [authStatus, setAuthStatus] = useState('checking'); // checking | in | out
  const [username, setUsername] = useState(null);

  useEffect(() => {
    if (!getAuthToken()) {
      setAuthStatus('out');
      return;
    }
    getCurrentUser()
      .then((data) => {
        setUsername(data.username);
        setAuthStatus('in');
      })
      .catch(() => setAuthStatus('out'));
  }, []);

  function handleLoggedIn(name) {
    setUsername(name);
    setAuthStatus('in');
  }

  function handleLogout() {
    clearAuthToken();
    setUsername(null);
    setAuthStatus('out');
  }

  return (
    <QueryClientProvider client={qc}>
      {authStatus === 'checking' && (
        <div style={{ minHeight: '100vh', background: '#f8fafc' }} />
      )}

      {authStatus === 'out' && <Login onLoggedIn={handleLoggedIn} />}

      {authStatus === 'in' && (
        <BrowserRouter>
          <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'system-ui, sans-serif' }}>
            {/* Top Nav */}
            <nav style={{ background: '#1e293b', padding: '0 24px', display: 'flex', alignItems: 'center', gap: 32 }}>
              <span style={{ color: '#f1f5f9', fontWeight: 700, fontSize: 18, padding: '16px 0' }}>
                IoT Alert Module
              </span>
              <div style={{ display: 'flex', gap: 4, flex: 1 }}>
                {NAV_ITEMS.map(item => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    style={({ isActive }) => ({
                      color: isActive ? '#38bdf8' : '#94a3b8',
                      textDecoration: 'none',
                      padding: '16px 14px',
                      fontSize: 14,
                      fontWeight: isActive ? 600 : 400,
                      borderBottom: isActive ? '3px solid #38bdf8' : '3px solid transparent',
                      display: 'inline-block',
                    })}
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <span style={{ color: '#94a3b8', fontSize: 13 }}>{username}</span>
                <button
                  onClick={handleLogout}
                  style={{
                    border: '1px solid #334155',
                    background: 'none',
                    color: '#e2e8f0',
                    borderRadius: 999,
                    padding: '6px 14px',
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  Logout
                </button>
              </div>
            </nav>

            {/* Page Content */}
            <main style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px' }}>
              <Routes>
                <Route path="/"         element={<AlertDashboard />} />
                <Route path="/settings" element={<AlertSettings  />} />
                <Route path="/devices"  element={<DeviceMappings />} />
                <Route path="/history"  element={<AlertHistory   />} />
                <Route path="/delivery" element={<DeliveryStatus />} />
              </Routes>
            </main>
          </div>
        </BrowserRouter>
      )}

      <Toaster position="top-right" />
    </QueryClientProvider>
  );
}
